import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useReference } from '@/lib/reference/use-reference'
import { Callout } from '@/components/ui/Callout'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'

/**
 * Frame shared by every reference table.
 *
 * Reference data is the part of the system that goes wrong quietly
 * (BUSINESS-RULES 11), so the frame states out loud which build of the seed
 * is loaded and how many rows have been changed away from it.
 */
export function SettingsLayout() {
  const { status, manifest, pending, reset } = useReference()
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [discarding, setDiscarding] = useState(false)

  if (status === 'loading') {
    return (
      <div className="grid min-h-[320px] place-items-center">
        <svg
          className="animate-spin text-primary"
          width="24"
          height="24"
          viewBox="0 0 16 16"
          fill="none"
          role="status"
          aria-label="Loading"
        >
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
          <path d="M14.5 8A6.5 6.5 0 0 0 8 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="max-w-[520px]">
        <Callout tone="error" title="Reference data unavailable">
          The seed files did not load.
        </Callout>
      </div>
    )
  }

  // Every table, so a new one cannot be added without its edits being counted.
  const changed = Object.values(pending).reduce((total, count) => total + count, 0)

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <p className="t-caption-strong text-muted">SETTINGS</p>
        {manifest && (
          <p className="t-caption text-muted">
            Seed {manifest.generatedAt.slice(0, 10)}
            {changed > 0 && (
              <>
                {' · '}
                <span className="text-ink">{changed} local</span>{' '}
                <Button
                  variant="tertiary"
                  className="!t-caption !h-auto !px-1"
                  onClick={() => setConfirmDiscard(true)}
                >
                  Discard
                </Button>
              </>
            )}
          </p>
        )}
      </div>

      <div className="mt-6">
        <Outlet />
      </div>

      {/* Discard drops every local change across every table at once, which
          is more than a row dialog ever does, so it asks first. */}
      {confirmDiscard && (
        <Modal title="Discard local changes" onClose={() => setConfirmDiscard(false)} width="420px">
          <p className="t-body-md mt-3 text-body">
            <span className="t-number-sm text-ink">{changed}</span> row
            {changed === 1 ? '' : 's'} across every table
          </p>

          <div className="mt-8 flex gap-3">
            <Button
              variant="secondary"
              fullWidth
              loading={discarding}
              className="text-semantic-down"
              onClick={() => {
                setDiscarding(true)
                reset()
                  .then(() => setConfirmDiscard(false))
                  .finally(() => setDiscarding(false))
              }}
            >
              Discard
            </Button>
            <Button variant="secondary" fullWidth onClick={() => setConfirmDiscard(false)}>
              Cancel
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
